/**
 * CrmEventTypeFilter - Filtro rápido por tipo de evento (Event Type)
 * 
 * Lista os event types cadastrados no tenant via useSchedulingEventTypes
 * e permite filtrar atividades por tipo de evento vinculado ao booking.
 * Segue o padrão visual do CrmSchedulingTypeFilter (Popover + Command).
 * 
 * @version 1.0.0
 * RELEASE: 2026-10-05 v1.1.0 - Gatilho do filtro segue o modelo de pill: sem a linha cinza da borda, fundo de superfície, sombra sutil e cantos arredondados (rounded-full).
 * RELEASE: 2026-02-15 v1.0.0 - Implementação inicial
 */

import React, { useState } from 'react';
import { Check, ChevronDown, CalendarClock } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { useSchedulingEventTypes } from '@/hooks/useSchedulingEventTypes';
import { Skeleton } from '@/components/ui/skeleton';
import { stripTemplateVariables } from '@/lib/schedulingUtils';

/** Props do componente CrmEventTypeFilter */
interface CrmEventTypeFilterProps {
  /** ID do event type selecionado (null = todos) */
  value: string | null;
  /** Callback para mudança de seleção */
  onChange: (eventTypeId: string | null) => void;
  /** Classes CSS adicionais */
  className?: string;
}

/**
 * Componente de filtro rápido por tipo de evento
 * Exibe lista de event types do tenant com opção "Todos"
 */
export function CrmEventTypeFilter({
  value,
  onChange,
  className,
}: CrmEventTypeFilterProps) {
  const [open, setOpen] = useState(false);
  const { eventTypes, isLoading } = useSchedulingEventTypes();

  // Detecta se é versão compacta baseado na classe
  const isCompact = className?.includes('h-[22px]');

  // Label do botão: nome do event type selecionado ou "Tipo Evento"
  const selectedLabel = value
    ? stripTemplateVariables(eventTypes.find(et => et.id === value)?.title || 'Tipo Evento')
    : 'Tipo Evento';

  // Handler para seleção
  const handleSelect = (eventTypeId: string | null) => {
    onChange(eventTypeId);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            'h-9 px-3 gap-2 font-normal border-0 bg-background rounded-full',
            className
          )}
        >
          <CalendarClock className={cn(isCompact ? 'h-3 w-3' : 'h-4 w-4', 'opacity-70')} />
          <span className="hidden sm:inline truncate max-w-[120px]">{selectedLabel}</span>
          <ChevronDown className={cn(isCompact ? 'h-2.5 w-2.5' : 'h-3 w-3', 'opacity-50')} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[220px] p-0" align="start">
        <Command>
          <CommandList>
            <CommandGroup heading="Tipo de Evento">
              {/* Opção "Todos" */}
              <CommandItem
                key="all"
                value="all"
                onSelect={() => handleSelect(null)}
                className="cursor-pointer"
              >
                <div
                  className={cn(
                    "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                    value === null
                      ? "border-primary bg-primary"
                      : "border-muted"
                  )}
                >
                  {value === null && (
                    <Check className="h-2.5 w-2.5 text-primary-foreground" />
                  )}
                </div>
                <CalendarClock className="h-4 w-4 mr-2 opacity-70" />
                <span>Todos</span>
              </CommandItem>

              {/* Loading skeleton */}
              {isLoading && (
                <div className="px-2 py-1.5 space-y-1">
                  <Skeleton className="h-6 w-full" />
                  <Skeleton className="h-6 w-3/4" />
                </div>
              )}

              {/* Lista de event types do tenant */}
              {eventTypes.map(et => (
                <CommandItem
                  key={et.id}
                  value={et.id}
                  onSelect={() => handleSelect(et.id)}
                  className="cursor-pointer"
                >
                  <div
                    className={cn(
                      "mr-2 h-4 w-4 rounded-full border flex items-center justify-center",
                      value === et.id
                        ? "border-primary bg-primary"
                        : "border-muted"
                    )}
                  >
                    {value === et.id && (
                      <Check className="h-2.5 w-2.5 text-primary-foreground" />
                    )}
                  </div>
                  {/* Bolinha de cor do event type */}
                  {et.color && (
                    <div
                      className="h-3 w-3 rounded-full mr-2 flex-shrink-0"
                      style={{ backgroundColor: et.color }}
                    />
                  )}
                  <span className="truncate">{stripTemplateVariables(et.title)}</span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
